import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import FormSuccess from "@/components/FormSuccess";
import { usePageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

const CONFIGS = {
  corporate: {
    eyebrow: "Corporate Travel",
    title: "Corporate travel enquiry",
    subtitle: "Dedicated account management, negotiated fares and 24/7 support for your business — tell us about your travel needs and we'll prepare a proposal.",
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=1920&auto=format&fit=crop",
    service: "Corporate Travel",
    submitLabel: "Request Corporate Proposal",
    waMessage: "Hello Makchester Tours & Travel, I have just submitted a corporate travel enquiry.",
    fields: [
      { k: "company", l: "Company Name", required: true, placeholder: "Acme Ltd" },
      { k: "full_name", l: "Contact Person", required: true, placeholder: "Jane Moyo" },
      { k: "phone", l: "Phone / WhatsApp", required: true, placeholder: "+263…" },
      { k: "email", l: "Work Email", type: "email" },
      { k: "volume", l: "Trips Per Month", type: "select", options: ["1–5", "6–15", "16–50", "50+"] },
      { k: "destinations", l: "Common Destinations", placeholder: "e.g. Dubai, Johannesburg, London" },
      { k: "notes", l: "Requirements", type: "textarea", span: 2, placeholder: "Flights, hotels, visas, airport transfers, travel policy needs…" },
    ],
  },
  insurance: {
    eyebrow: "Travel Insurance",
    title: "Travel insurance enquiry",
    subtitle: "Comprehensive cover for medical emergencies, cancellations and baggage — including Schengen-compliant policies from €30,000 medical cover.",
    image: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?q=80&w=1920&auto=format&fit=crop",
    service: "Travel Insurance",
    submitLabel: "Get Insurance Quote",
    waMessage: "Hello Makchester Tours & Travel, I have just submitted a travel insurance enquiry.",
    fields: [
      { k: "full_name", l: "Full Name", required: true },
      { k: "phone", l: "Phone / WhatsApp", required: true, placeholder: "+263…" },
      { k: "email", l: "Email", type: "email" },
      { k: "destination", l: "Destination(s)", required: true, placeholder: "e.g. Schengen / Dubai" },
      { k: "travel_date", l: "Trip Start Date", type: "date" },
      { k: "return_date", l: "Trip End Date", type: "date" },
      { k: "travellers", l: "Travellers", type: "select", options: ["1", "2", "3", "4", "5", "6+"] },
      { k: "cover", l: "Cover Type", type: "select", options: ["Single Trip", "Multi-Trip (Annual)", "Family Cover", "Schengen-Compliant", "Not sure — advise me"] },
      { k: "notes", l: "Notes", type: "textarea", span: 2, placeholder: "Pre-existing conditions, adventure activities, gadget cover…" },
    ],
  },
  transfers: {
    eyebrow: "Airport Transfers",
    title: "Airport transfer booking",
    subtitle: "Premium private transfers across the UAE, Zimbabwe and South Africa — meet & greet, flight tracking and fixed prices.",
    image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=1920&auto=format&fit=crop",
    service: "Airport Transfer",
    submitLabel: "Book My Transfer",
    waMessage: "Hello Makchester Tours & Travel, I have just submitted an airport transfer booking request.",
    fields: [
      { k: "full_name", l: "Full Name", required: true },
      { k: "phone", l: "Phone / WhatsApp", required: true, placeholder: "+971…" },
      { k: "email", l: "Email", type: "email" },
      { k: "flight_number", l: "Flight Number", placeholder: "e.g. EK713" },
      { k: "pickup", l: "Pickup Location", required: true, placeholder: "e.g. Dubai International (DXB)" },
      { k: "dropoff", l: "Drop-off Location", required: true, placeholder: "e.g. Palm Jumeirah hotel" },
      { k: "travel_date", l: "Date", type: "date", required: true },
      { k: "time", l: "Pickup Time", type: "time" },
      { k: "passengers", l: "Passengers", type: "select", options: ["1", "2", "3", "4", "5", "6", "7+"] },
      { k: "vehicle", l: "Vehicle Type", type: "select", options: ["Sedan", "SUV", "Van / Minibus", "Luxury"] },
      { k: "notes", l: "Notes", type: "textarea", span: 2, placeholder: "Child seats, extra luggage, return transfer…" },
    ],
  },
};

const inputCls = "w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A62A]";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-1.5 block";

export default function ServiceEnquiry({ type }) {
  const cfg = CONFIGS[type];
  usePageMeta(cfg.title.charAt(0).toUpperCase() + cfg.title.slice(1), cfg.subtitle);
  const empty = Object.fromEntries(cfg.fields.map((f) => [f.k, f.type === "select" ? f.options[0] : ""]));
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const extras = cfg.fields
        .filter((x) => !["full_name", "phone", "email", "travel_date", "notes"].includes(x.k) && f[x.k])
        .map((x) => `${x.l}: ${f[x.k]}`).join(", ");
      await api.post("/public/contact", {
        full_name: f.full_name, phone: f.phone, email: f.email || "",
        service_needed: cfg.service, travel_date: f.travel_date || "",
        notes: `${cfg.service} enquiry — ${extras}${f.notes ? `. Notes: ${f.notes}` : ""}`,
      });
      trackEvent("enquiry_submitted", { form: type });
      setDone(true);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white">
      <section className="relative py-28 bg-[#0A2E4D] overflow-hidden">
        <img src={cfg.image} alt={cfg.eyebrow} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="mk-gold-line mb-6 inline-block" />
          <p className="text-xs uppercase tracking-[0.25em] text-[#E3BA45] font-bold mb-3">{cfg.eyebrow}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-white" data-testid={`${type}-page-heading`}>{cfg.title}</h1>
          <p className="text-slate-300 mt-5 max-w-xl mx-auto leading-relaxed">{cfg.subtitle}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <FormSuccess waMessage={cfg.waMessage} onReset={() => { setDone(false); setF(empty); }} testId={`${type}-form-success`} />
          ) : (
            <form onSubmit={submit} className="mk-card !transform-none p-8 grid grid-cols-2 gap-4" data-testid={`${type}-enquiry-form`}>
              {cfg.fields.map((x) => (
                <div key={x.k} className={x.span === 2 ? "col-span-2" : "col-span-2 sm:col-span-1"}>
                  <label className={labelCls}>{x.l}{x.required && <span className="text-[#EF4444] ml-1">*</span>}</label>
                  {x.type === "textarea" ? (
                    <textarea rows={4} value={f[x.k]} onChange={set(x.k)} className={inputCls} placeholder={x.placeholder} data-testid={`${type}-${x.k}-input`} />
                  ) : x.type === "select" ? (
                    <select value={f[x.k]} onChange={set(x.k)} className={inputCls} data-testid={`${type}-${x.k}-select`}>
                      {x.options.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={x.type || "text"} required={x.required} value={f[x.k]} onChange={set(x.k)} className={inputCls} placeholder={x.placeholder} data-testid={`${type}-${x.k}-input`} />
                  )}
                </div>
              ))}
              <button type="submit" disabled={busy} className="mk-btn-gold col-span-2 !py-4 disabled:opacity-60" data-testid={`${type}-submit-btn`}>
                {busy ? "Sending…" : cfg.submitLabel}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
